import React from 'react';
import { Modal, Button, Table, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ApiResponse } from 'services/api/base';
import services from 'services';
import { FaHistory, FaClock, FaUser, FaPaperclip, FaFileAlt, FaDownload, FaHourglassHalf } from 'react-icons/fa';
import Pager from 'components/common/Pager';
import * as models from 'models';
import AiReportViewerModal from './AiReportViewerModal';

interface Props {
    toolId: number;
    toolName?: string;
    onClose: () => void;
}

interface HistoryItem {
    logId: number;
    executedBy: string;
    executionDate: string;
    executionCompletedDate?: string | null;
    duration?: string | null;
    durationSeconds?: number | null;
    summary: string;
    attachmentsCount: number;
    status?: string;
}

interface State {
    historyLogs: HistoryItem[];
    isLoading: boolean;
    isExporting: boolean;
    error: string | null;
    selectedReportId: number | null;
    pageNo: number;
    pageSize: number;
    totalCount: number;
}

const DEFAULT_PAGE_SIZE = 20;

export class AiToolHistoryModal extends React.Component<Props, State> {
    public state: State = {
        historyLogs: [],
        isLoading: true,
        isExporting: false,
        error: null,
        selectedReportId: null,
        pageNo: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalCount: 0
    };

    public componentDidMount() {
        this.loadHistory(1, DEFAULT_PAGE_SIZE);
    }

    private loadHistory = async (pageNo: number, pageSize: number) => {
        this.setState({ isLoading: true, error: null });

        try {
            const response: ApiResponse = await services.api.aiLab.fetchToolExecutionHistory(
                this.props.toolId,
                pageNo,
                pageSize
            );

            if (response.isSuccess) {
                const body = response.data || {};
                const logs = body.resourceList || [];
                this.setState({
                    historyLogs: logs,
                    pageNo: body.pageNo || pageNo,
                    pageSize: body.pageSize || pageSize,
                    totalCount: body.totalCount || 0,
                    isLoading: false
                });
            } else {
                this.setState({
                    error: response.getError() || 'Failed to extract historical log index.',
                    isLoading: false
                });
            }
        } catch (err: any) {
            this.setState({ error: err.message, isLoading: false });
        }
    };

    private handlePageChange = (paging: models.Paging) => {
        const pageSizeChanged = paging.pageSize !== this.state.pageSize;
        const pageNo = pageSizeChanged ? 1 : paging.pageNo;
        this.loadHistory(pageNo, paging.pageSize);
    };

    private handleRowClick = (logId: number) => {
        this.setState({ selectedReportId: logId });
    };

    private handleCloseReportViewer = () => {
        this.setState({ selectedReportId: null });
    };

    private escapeCsv = (value: string | number | null | undefined): string => {
        const text = value == null ? '' : String(value);
        if (/[",\n\r]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
    };

    private buildCsv = (logs: HistoryItem[]): string => {
        const header = ['Log ID', 'Run Date', 'Completed', 'Duration', 'Duration (seconds)', 'Run By', 'Status', 'Assets', 'Summary'];
        const rows = logs.map((log) => [
            this.escapeCsv(log.logId),
            this.escapeCsv(log.executionDate),
            this.escapeCsv(log.executionCompletedDate),
            this.escapeCsv(log.duration ?? ''),
            this.escapeCsv(log.durationSeconds ?? ''),
            this.escapeCsv(log.executedBy),
            this.escapeCsv(this.formatStatusLabel(log.status)),
            this.escapeCsv(log.attachmentsCount),
            this.escapeCsv(log.summary)
        ].join(','));

        return [header.join(','), ...rows].join('\r\n');
    };

    private handleExportCsv = async () => {
        const { totalCount, isExporting } = this.state;
        if (isExporting || totalCount === 0) {
            return;
        }

        this.setState({ isExporting: true, error: null });

        try {
            // pageSize 0 asks the API for the full history, independent of the grid page
            const response: ApiResponse = await services.api.aiLab.fetchToolExecutionHistory(
                this.props.toolId,
                1,
                0
            );

            if (!response.isSuccess) {
                this.setState({
                    error: response.getError() || 'Failed to export execution history.',
                    isExporting: false
                });
                return;
            }

            const logs: HistoryItem[] = response.data?.resourceList || [];
            if (!logs.length) {
                this.setState({ isExporting: false });
                return;
            }

            const blob = new Blob([this.buildCsv(logs)], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const safeName = (this.props.toolName || `tool-${this.props.toolId}`)
                .replace(/[^\w\-]+/g, '_')
                .replace(/_+/g, '_')
                .slice(0, 60);
            link.href = url;
            link.download = `ai-tool-history_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            this.setState({ isExporting: false });
        } catch (err: any) {
            this.setState({ error: err.message, isExporting: false });
        }
    };

    private normalizeStatus = (status?: string): string =>
        (status || 'unknown').trim().toLowerCase();

    private formatStatusLabel = (status?: string): string => {
        switch (this.normalizeStatus(status)) {
            case 'completed':
                return 'Completed';
            case 'failed':
                return 'Failed';
            case 'queued':
            case 'pending':
                return 'Queued';
            case 'processing':
                return 'Processing';
            default:
                return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
        }
    };

    private getStatusBadgeClass = (status?: string): string => {
        switch (this.normalizeStatus(status)) {
            case 'completed':
                return 'bg-success';
            case 'failed':
                return 'bg-danger';
            case 'processing':
                return 'bg-info';
            case 'queued':
            case 'pending':
                return 'bg-secondary';
            default:
                return 'bg-light text-dark border';
        }
    };

    private renderStatusBadge = (log: HistoryItem) => {
        const normalized = this.normalizeStatus(log.status);
        const badge = (
            <span className={`badge ${this.getStatusBadgeClass(log.status)}`}>
                {this.formatStatusLabel(log.status)}
            </span>
        );

        // Failures store the error text in HighLevelSummary ("Execution Failed: ...")
        if (normalized === 'failed' && log.summary) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip
                            id={`history-status-${log.logId}`}
                            style={{ maxWidth: '480px', textAlign: 'left' }}
                        >
                            {log.summary}
                        </Tooltip>
                    }
                >
                    <span
                        className="d-inline-block"
                        style={{ cursor: 'help' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {badge}
                    </span>
                </OverlayTrigger>
            );
        }

        return badge;
    };

    public render() {
        const { historyLogs, isLoading, isExporting, error, selectedReportId, pageNo, pageSize, totalCount } = this.state;
        const canExport = !isLoading && !isExporting && !error && totalCount > 0;
        const showPager = !isLoading && !error && totalCount > 0;

        return (
            <>
                <Modal
                    centered={true}
                    show={true}
                    onHide={this.props.onClose}
                    size="xl"
                    backdrop="static"
                    dialogClassName="modal-xl"
                >
                    <Modal.Header closeButton={true}>
                        <Modal.Title className="d-flex align-items-center text-dark fw-bold">
                            <FaHistory className="me-2 text-info" style={{ marginRight: '8px' }} /> Pipeline Execution Log Archive
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '350px', maxHeight: '75vh', overflowY: 'auto' }}>
                        
                        {isLoading && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <Spinner animation="border" variant="info" className="mb-3" />
                                <h6 className="fw-bold text-muted">Compiling Historical Execution Bundles...</h6>
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger border-0 shadow-sm">
                                <h6 className="fw-bold">History Log Error Intercepted</h6>
                                <p className="small mb-0">{error}</p>
                            </div>
                        )}

                        {!isLoading && !error && totalCount === 0 && (
                            <div className="text-center bg-white p-5 rounded border shadow-sm">
                                <FaHistory size={40} className="text-muted mb-3" />
                                <h6 className="fw-bold text-secondary">No Historical Executions Recorded</h6>
                                <p className="small text-muted mb-0">This tool configuration bundle has not been executed globally yet.</p>
                            </div>
                        )}

                        {!isLoading && !error && historyLogs.length > 0 && (
                            <div className="bg-white rounded border shadow-sm overflow-hidden">
                                <Table hover responsive className="mb-0 align-middle small">
                                    <thead className="table-light text-uppercase text-muted">
                                        <tr>
                                            <th style={{ width: '175px', whiteSpace: 'nowrap' }}><FaClock className="me-1" /> Run Date</th>
                                            <th style={{ width: '100px' }}>Status</th>
                                            <th style={{ width: '90px' }}><FaHourglassHalf className="me-1" /> Duration</th>
                                            <th style={{ width: '140px' }}><FaUser className="me-1" /> Run By</th>
                                            <th>Summary</th>
                                            <th style={{ width: '70px' }} className="text-center"><FaPaperclip /> Assets</th>
                                            <th style={{ width: '70px' }} className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyLogs.map((log) => (
                                            <tr 
                                                key={log.logId} 
                                                onClick={() => this.handleRowClick(log.logId)}
                                                style={{ cursor: 'pointer' }}
                                                className="transition-hover"
                                            >
                                                <td className="text-dark fw-bold" style={{ whiteSpace: 'nowrap' }}>{log.executionDate}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {this.renderStatusBadge(log)}
                                                </td>
                                                <td className="text-secondary fw-bold" style={{ whiteSpace: 'nowrap' }}>
                                                    {log.duration ?? '—'}
                                                </td>
                                                <td className="text-secondary">{log.executedBy}</td>
                                                <td className="text-secondary" style={{ maxWidth: '420px' }}>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip
                                                                id={`history-summary-${log.logId}`}
                                                                style={{ maxWidth: '480px', textAlign: 'left' }}
                                                            >
                                                                {log.summary || 'No summary available.'}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="d-inline-block text-truncate" style={{ maxWidth: '100%', verticalAlign: 'bottom' }}>
                                                            {log.summary}
                                                        </span>
                                                    </OverlayTrigger>
                                                </td>
                                                <td className="text-center fw-bold">
                                                    <span className={`badge ${log.attachmentsCount > 0 ? 'bg-light text-dark border' : 'bg-light text-muted'}`}>
                                                        {log.attachmentsCount}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <Button 
                                                        color="info" 
                                                        size="sm" 
                                                        className="py-1 px-2 text-white fw-bold shadow-sm btn-xs d-inline-flex align-items-center justify-content-center"
                                                        style={{ color: '#ffffff', minHeight: '24px' }}
                                                        title="Open specific historical report details"
                                                    >
                                                        <FaFileAlt size={11} className="me-1" style={{ marginRight: '4px' }} /> VIEW
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                    </Modal.Body>
                    <Modal.Footer className="d-flex flex-column align-items-stretch py-2">
                        {showPager && (
                            <Pager
                                key={`history-pager-${pageNo}-${pageSize}`}
                                className="admin-pager w-100 mb-2"
                                retrieveDataCallback={this.handlePageChange}
                                pageSize={pageSize}
                                currentPage={pageNo}
                                totalCount={totalCount}
                                pagesInRange={3}
                            />
                        )}
                        <div className="d-flex justify-content-between w-100">
                            <Button
                                size="sm"
                                className="py-1 px-2 text-white fw-bold shadow-sm btn-xs d-inline-flex align-items-center justify-content-center"
                                style={{ color: '#ffffff', minHeight: '24px', backgroundColor: '#0dcaf0', borderColor: '#0dcaf0' }}
                                onClick={this.handleExportCsv}
                                disabled={!canExport}
                                title="Download all execution history as CSV"
                            >
                                {isExporting
                                    ? <Spinner animation="border" size="sm" className="me-1" />
                                    : <FaDownload size={11} className="me-1" style={{ marginRight: '4px' }} />}
                                EXPORT
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="py-1 px-3 fw-bold"
                                onClick={this.props.onClose}
                            >
                                CLOSE
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {selectedReportId !== null && (
                    <AiReportViewerModal 
                        reportId={selectedReportId} 
                        onClose={this.handleCloseReportViewer} 
                    />
                )}
            </>
        );
    }
}

export default AiToolHistoryModal;
